import runes from "@-/com.npmjs.runes2";
import type { PronouncedGraphemes } from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.pronunciation.read";
import { buildUnitSequence, isSequenceEntryLearned } from "@-/dev.improve.haniverse.lib.lesson.get";
import { formatPronunciationLevelLabel, type Level } from "@-/dev.improve.haniverse.lib.level";
import type { Quest, QuestPhrase } from "#quest-types";

export type QuestHanziScript = "simplified" | "traditional";

export interface QuestUnlock {
  isUnlocked: boolean;
  /** What unlocks it ("Unlocks after Level 2-P2"), or `null` when nothing has to be learned. */
  label: string | null;
}

/** The Mandarin a quest asks the learner to say. */
export function getQuestAnswers(quest: Quest): QuestPhrase[] {
  return quest.steps.flatMap((step) => (step.kind === "reply" ? [step.answer] : []));
}

/**
 * Whether the learner has learned what a quest needs. A pronunciation-level requirement is met
 * once every lesson of that level is learned, by the same rule the lesson sequence uses
 * (`isSequenceEntryLearned`). A script without that pronunciation level (traditional has none)
 * falls back to every character of the quest's answers being studied.
 */
export function getQuestUnlock({
  hanziScript,
  levels,
  pronounced,
  quest,
  studiedGraphemes,
}: {
  hanziScript: QuestHanziScript;
  /** The learner's script's levels (`useLevels`). */
  levels: readonly Level[];
  pronounced: PronouncedGraphemes;
  quest: Quest;
  /** Graphemes with a card in study or mastered (`useBrowseLevelsStudyGraphemesQuery`). */
  studiedGraphemes: ReadonlySet<string>;
}): QuestUnlock {
  const { requirement } = quest;
  if (requirement.kind === "none") {
    return { isUnlocked: true, label: null };
  }
  const pronunciationLevel = levels.filter((level) => level.pronunciationCharacters !== undefined)[
    requirement.pronunciationIndex
  ];
  if (pronunciationLevel === undefined) {
    const words = getQuestAnswers(quest).map((phrase) => phrase[hanziScript]);
    return {
      isUnlocked: words.every((word) =>
        runes(word).every(
          (character) => studiedGraphemes.has(character) || pronounced.graphemes.has(character),
        ),
      ),
      label: `Unlocks once you learn ${words.join(", ")}`,
    };
  }
  const isUnlocked = buildUnitSequence(levels).every(
    (entry) =>
      entry.type !== "morpheme" ||
      entry.part !== "pronunciation" ||
      entry.level.index !== pronunciationLevel.index ||
      isSequenceEntryLearned(entry, studiedGraphemes, pronounced),
  );
  return {
    isUnlocked,
    label: `Unlocks after Level ${formatPronunciationLevelLabel(
      pronunciationLevel,
      requirement.pronunciationIndex,
    )}`,
  };
}
